// Sdílené výpočty nabídky — zrcadlí backend routes/nabidky.py compute().
// Zaokrouhlení až na úrovni rekapitulace DPH (ne po řádcích), aby to sedělo na halíř.
export const r2 = (x) => Math.round(((Number(x) || 0) + Number.EPSILON) * 100) / 100;

// Pevná prodejní cena pro klienta (`cena_prodej`) má přednost před nákup × marže.
// POZOR: 0 je platná cena (položka zdarma) — testuje se null/undefined.
export const maPevnou = (r) => r.cena_prodej !== null && r.cena_prodej !== undefined && r.cena_prodej !== "";
export const znamyNakup = (r) => r.cena_nakup !== null && r.cena_nakup !== undefined && r.cena_nakup !== "";

export function prodejJednotka(r) {
  if (maPevnou(r)) return r2(r.cena_prodej);
  const nakup = Number(r.cena_nakup) || 0;
  const marze = Number(r.marze_pct) || 0;
  return r2(nakup * (1 + marze / 100));
}

export function radekCelkem(r) {
  const mnozstvi = Number(r.mnozstvi) || 0;
  return r2(prodejJednotka(r) * mnozstvi);
}

// Marže řádku tak, jak opravdu vychází (u pevné ceny dopočtená). Bez nákupu null.
export function marzeRadku(r) {
  if (!znamyNakup(r) || !(Number(r.cena_nakup) > 0)) return maPevnou(r) ? null : Number(r.marze_pct) || 0;
  if (!maPevnou(r)) return Number(r.marze_pct) || 0;
  return Math.round((prodejJednotka(r) / Number(r.cena_nakup) - 1) * 1000) / 10;
}

// Sleva řádku = o kolik je nákup levnější než prodej, v % z prodeje
// (prodej 10 000, nákup 7 000 → sleva 30 %). Je to druhý pohled na marži:
// sleva jde z prodeje dolů na nákup, marže z nákupu nahoru na prodej
// (sleva 30 % = marže 42,9 %). Bez nákupu nebo prodeje null.
export function slevaRadku(r) {
  if (!znamyNakup(r)) return null;
  const prodej = prodejJednotka(r);
  if (!(prodej > 0)) return null;
  return Math.round(((prodej - Number(r.cena_nakup)) / prodej) * 1000) / 10;
}
export const nakupZeSlevy = (prodej, sleva) => r2(Number(prodej) * (1 - Number(sleva) / 100));

// Sazba DPH je volné číslo 0–100 %. POZOR: 0 % je platná sazba (přenesená
// daňová povinnost) — nikde se nesmí použít `Number(x) || default`, nula je
// falsy a tiše by se přepsala na výchozí sazbu.
export const validDph = (v) => v !== null && v !== undefined && v !== ""
  && Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 100;
export const dphOr = (v, def) => (validDph(v) ? Number(v) : def);

export const ETAPA_RIM = ["—", "I", "II", "III"];
export const ETAPA_NAZEV = ["Bez etapy", "I. etapa", "II. etapa", "III. etapa"];

function prevailingSazba(rows, def) {
  const by = {};
  for (const r of rows) {
    const s = dphOr(r.dph_sazba_pct, def);
    by[s] = (by[s] || 0) + radekCelkem(r);
  }
  const keys = Object.keys(by).map(Number);
  if (!keys.length) return def;
  keys.sort((a, b) => by[b] - by[a] || b - a);
  return keys[0];
}

// Sleva pod čarou v Kč — z % se počítá ze součtu, nikdy víc než součet.
export function slevaKc(sleva, soucet) {
  const h = Number(sleva?.hodnota) || 0;
  if (h <= 0 || soucet <= 0) return 0;
  const kc = sleva.typ === "PCT" ? (soucet * h) / 100 : h;
  return r2(Math.min(kc, soucet));
}

export function souctyNabidky(rows, doprava, defaultSazba, sleva = null) {
  const def = dphOr(defaultSazba, 21);
  const sekce = { MATERIAL: 0, PRACE: 0, OSTATNI: 0 };
  const zakladBySazba = {};
  const etapy = {};
  for (const r of rows) {
    const celkem = radekCelkem(r);
    sekce[r.typ] = r2((sekce[r.typ] || 0) + celkem);
    const s = dphOr(r.dph_sazba_pct, def);
    zakladBySazba[s] = r2((zakladBySazba[s] || 0) + celkem);
    const e = Number(r.etapa) || 0;
    if (e) {
      etapy[e] = etapy[e] || { MATERIAL: 0, PRACE: 0, OSTATNI: 0 };
      etapy[e][r.typ] = r2(etapy[e][r.typ] + celkem);
    }
  }
  const d = doprava || {};
  const dopravaCelkem = r2((Number(d.vzdalenost_km) || 0) * (Number(d.pocet_jizd) || 0) * (Number(d.sazba_kc_km) || 0));
  const dopravaSazba = validDph(d.dph_sazba_pct) ? Number(d.dph_sazba_pct) : prevailingSazba(rows, def);
  if (dopravaCelkem) zakladBySazba[dopravaSazba] = r2((zakladBySazba[dopravaSazba] || 0) + dopravaCelkem);

  // Sleva poměrně do všech sazeb; haléřový rozdíl do největšího základu.
  const soucet = r2(Object.values(zakladBySazba).reduce((a, x) => a + x, 0));
  const sl = slevaKc(sleva, soucet);
  let poSleve = { ...zakladBySazba };
  if (sl && soucet) {
    const koef = (soucet - sl) / soucet;
    poSleve = Object.fromEntries(Object.entries(zakladBySazba).map(([s, z]) => [s, r2(z * koef)]));
    const rozdil = r2(soucet - sl - Object.values(poSleve).reduce((a, x) => a + x, 0));
    if (rozdil) {
      const nej = Object.keys(poSleve).sort((a, b) => poSleve[b] - poSleve[a] || b - a)[0];
      poSleve[nej] = r2(poSleve[nej] + rozdil);
    }
  }
  const rekapitulace = Object.keys(poSleve).map(Number).sort((a, b) => a - b)
    .map((s) => ({ sazba: s, zaklad: poSleve[s], dph: r2((poSleve[s] * s) / 100) }));
  const zaklad = r2(rekapitulace.reduce((a, x) => a + x.zaklad, 0));
  const dph = r2(rekapitulace.reduce((a, x) => a + x.dph, 0));

  // Marže jen z řádků se známým nákupem, po slevě.
  const koef = soucet ? zaklad / soucet : 1;
  const znama = rows.filter(znamyNakup);
  const prodejZnamy = znama.reduce((a, r) => a + radekCelkem(r), 0) * koef;
  const nakupZnamy = znama.reduce((a, r) => a + (Number(r.cena_nakup) || 0) * (Number(r.mnozstvi) || 0), 0);
  const zisk = r2(prodejZnamy - nakupZnamy);

  return {
    mezisoucet_material: sekce.MATERIAL,
    mezisoucet_prace: sekce.PRACE,
    mezisoucet_ostatni: sekce.OSTATNI,
    doprava_celkem: dopravaCelkem,
    doprava_dph_sazba_pct: dopravaSazba,
    soucet, sleva_kc: sl,
    rekapitulace, zaklad, dph, celkem_s_dph: r2(zaklad + dph),
    etapy: Object.keys(etapy).map(Number).sort().map((e) => ({
      etapa: e, nazev: ETAPA_NAZEV[e], material: etapy[e].MATERIAL, prace: etapy[e].PRACE, ostatni: etapy[e].OSTATNI,
      celkem: r2(etapy[e].MATERIAL + etapy[e].PRACE + etapy[e].OSTATNI) })),
    zisk, marze_pct: prodejZnamy ? Math.round((zisk / prodejZnamy) * 1000) / 10 : null,
    bez_nakupu: rows.length - znama.length,
  };
}
